import { useEffect, useState } from 'react';

import {
  fetchRecordGroups,
  toProblemDetail
} from '../../../services/syntiro_honey/data_ingestion.service';
import { ProblemDetail, RecordGroup, RecordGroupsQuery } from '../../../types/syntiro_honey';

/** State exposed by {@link useRecordGroups}. */
export interface RecordGroupsState {
  /** The groups for the current query, or null before the first load completes. */
  groups: Array<RecordGroup> | null;
  /** Whether a groups request is in flight. */
  loading: boolean;
  /** The problem detail of the last failed request, or null on success. */
  problem: ProblemDetail | null;
}

/**
 * Fetches the collapsible review roll-up for the given grouping + filters,
 * refetching whenever the query (serialised) or `refreshKey` changes. The query is
 * compared by value, so the caller need not memoise it. Stale responses are
 * discarded so rapid changes cannot overwrite newer results.
 * @param {RecordGroupsQuery} query Filters + groupBy for `GET /records/groups`.
 * @param {number} refreshKey Bump to force a refetch (e.g. after a mutation).
 * @returns {RecordGroupsState}
 */
export function useRecordGroups(query: RecordGroupsQuery, refreshKey: number): RecordGroupsState {
  const [groups, setGroups] = useState<Array<RecordGroup> | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [problem, setProblem] = useState<ProblemDetail | null>(null);
  // Compare the query by value so the caller can pass a fresh object each render.
  const queryKey = JSON.stringify(query);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchRecordGroups(query)
      .then((result) => {
        if (!cancelled) {
          setGroups(result.groups);
          setProblem(null);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setProblem(toProblemDetail(error));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // `query` is captured via its serialised form `queryKey` to avoid identity churn.
  }, [queryKey, refreshKey]);

  return { groups, loading, problem };
}
